import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** The page's scroll region (StandardLayout's `data-page-scroller`). */
export const PAGE_SCROLLER_SELECTOR = '[data-page-scroller]';

/** A section counts as reached once its top is this close to the top of the scroll region (px, measured). */
export const SPY_REACHED_PX = 32;

/** After a click on a section entry, that entry stays highlighted this long, while the smooth scroll runs. */
export const SPY_HOLD_MS = 700;

/** One section as the spy measures it: its top against the scroll region's top, or null when it is not on the page. */
export interface SpiedSection {
  id: string;
  top: number | null;
}

/**
 * Which section the side menu highlights (web.md §6.1): at the very bottom of a page that scrolled, the
 * last one; else the last section whose top has reached the top of the scroll region; else the first.
 * Sections not on the page yet (still loading) are passed over. Pure.
 */
export function pickActiveSection(sections: readonly SpiedSection[], atBottom: boolean): string | null {
  const present = sections.filter((section) => section.top !== null);
  if (present.length === 0) return sections[0]?.id ?? null;
  if (atBottom) return present[present.length - 1].id;
  let active = present[0].id;
  for (const section of present) {
    if ((section.top as number) <= SPY_REACHED_PX) active = section.id;
  }
  return active;
}

/** The scroll region that holds `anchor`, if it is in one. */
export function pageScrollerOf(anchor: Element | null): HTMLElement | null {
  return anchor?.closest<HTMLElement>(PAGE_SCROLLER_SELECTOR) ?? null;
}

/**
 * The side menu's scroll-spy (spec:278): the section nearest the top of the page as the customer
 * scrolls. A passive scroll listener on the page's scroll region, one measure per animation frame; no
 * IntersectionObserver (jsdom has none, and nothing here uses one). Each section is found by its id.
 *
 * `hold(id)` is for a click on a section entry: the clicked entry stays highlighted for SPY_HOLD_MS, so
 * the entries the smooth scroll passes on its way do not flicker.
 */
export function useScrollSpy(
  ids: readonly string[],
  anchor: RefObject<HTMLElement | null>,
): { activeId: string | null; hold: (id: string) => void } {
  const key = ids.join(' ');
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  const held = useRef<{ id: string; until: number } | null>(null);

  useEffect(() => {
    const list = key === '' ? [] : key.split(' ');
    const scroller = pageScrollerOf(anchor.current);
    let frame: number | null = null;

    const measure = () => {
      frame = null;
      const hold = held.current;
      if (hold && Date.now() < hold.until) {
        setActiveId(hold.id);
        return;
      }
      held.current = null;
      const scrollerTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const sections = list.map((id) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top - scrollerTop : null };
      });
      // Only a page that has scrolled can be at its bottom: on one too short to scroll, every section
      // is in view and the first is the one at the top.
      const atBottom =
        !!scroller && scroller.scrollTop > 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      setActiveId(pickActiveSection(sections, atBottom));
    };
    const onScroll = () => {
      if (frame !== null) return;
      frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(measure) : null;
      if (frame === null) measure();
    };

    held.current = null;
    measure();
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (frame !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    };
  }, [key, anchor]);

  const hold = useCallback((id: string) => {
    held.current = { id, until: Date.now() + SPY_HOLD_MS };
    setActiveId(id);
  }, []);

  return { activeId, hold };
}
